import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper,
  Container
} from '@mui/material';
import { 
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  ErrorOutline as ErrorIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const NotFoundPage = ({ 
  title = "Page Not Found", 
  message = "The page you're looking for doesn't exist.",
  customBackPath = null,
  customBackText = null 
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Function to determine back navigation based on current path
  const getBackNavigation = () => {
    const path = location.pathname;
    
    // If custom back path is provided, use it
    if (customBackPath) {
      return {
        path: customBackPath,
        text: customBackText || 'Go Back'
      };
    }

    // Analyze current path and provide appropriate back navigation
    if (path.includes('/leads/') && path.includes('/overview')) {
      // If on lead overview page, go back to leads list
      return {
        path: '/leads',
        text: 'Go Back to Leads'
      };
    } else if (path.includes('/leads/')) {
      // If on any lead detail page, go back to leads list
      return {
        path: '/leads',
        text: 'Go Back to Leads'
      };
    } else if (path.includes('/calls/')) {
      // If on calls page, go back to calls list
      return {
        path: '/calls',
        text: 'Go Back to Calls'
      };
    } else if (path.includes('/agents/')) {
      // If on agents page, go back to agents list
      return {
        path: '/agents',
        text: 'Go Back to Agents'
      };
    } else if (path.includes('/reports/')) {
      // If on reports page, go back to reports
      return {
        path: '/reports',
        text: 'Go Back to Reports'
      };
    } else if (path.includes('/settings/')) {
      // If on settings page, go back to settings
      return {
        path: '/settings',
        text: 'Go Back to Settings'
      };
    } else {
      // Default fallback to home
      return {
        path: '/',
        text: 'Go to Home'
      };
    }
  };

  const backNavigation = getBackNavigation();

  const handleBackClick = () => {
    navigate(backNavigation.path);
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            maxWidth: 500,
            width: '100%'
          }}
        >
          {/* Error Icon */}
          <Box sx={{ mb: 3 }}>
            <ErrorIcon 
              sx={{ 
                fontSize: 80, 
                color: 'error.main',
                opacity: 0.8
              }} 
            />
          </Box>

          {/* 404 Number */}
          <Typography
            variant="h1"
            sx={{
              fontSize: '6rem',
              fontWeight: 700,
              color: 'text.primary',
              lineHeight: 1,
              mb: 2,
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            404
          </Typography>

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 2
            }}
          >
            {title}
          </Typography>

          {/* Message */}
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 4,
              lineHeight: 1.6,
              fontSize: '1.1rem'
            }}
          >
            {message}
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* Back Button */}
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              {backNavigation.text}
            </Button>

            {/* Home Button */}
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={handleHomeClick}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Go to Home
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default NotFoundPage; 